import React, { useState, useEffect, useCallback, useMemo, SyntheticEvent, useRef } from "react";
import {
  Button,
  Checkbox,
  Form,
  FormField,
  FormGroup,
  Icon,
  Modal,
  Pagination,
  Popup,
  Tab,
  TabProps,
  TextArea,
} from "semantic-ui-react";
import { useIntl } from "react-intl";
// UX
import CardAppointmentListMEHUX from "react-lib/apps/MEH/CardAppointmentListMEHUX";

// Utils
import SearchBoxDropdown, { SelectedItemHandler } from "react-lib/appcon/common/SearchBoxDropdown";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import {
  ACTIONS,
  appointmentStatusOptions,
  appointmentStatusType,
  BTN_ACTS,
  orderStatusOptions,
  PickedState,
  RunSequence,
  serviceStatusType,
  SetProp,
  State,
} from "react-lib/apps/MEH/sequence/AppointmentListMEH";
import EmployeeToken from "react-lib/apps/common/EmployeeToken";
import ErrorMessage from "react-lib/component-js/common/ErrorMessage";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import ModInfoOA from "../IsHealth/REG/ModInfoOA";
import CardAppointmentDetailMEH from "react-lib/apps/MEH/CardAppointmentDetailMEH";

// SCSS
import "./CardAppointmentListMEH.scss";

// Config
import CardMakeAppointmentMeHey from "./appointments/CardMakeAppointmentMeHey";

type CardAppointmentListMEHProps = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  setProp: SetProp;
  runSequence: RunSequence;
  onEvent: (e: any) => Promise<any>;
  django?: Record<any, string>;
  currentDoctor?: Record<any, string>;
  controller?: any;

  // sequence
  AppointmentListMEHSequence?: State["AppointmentListMEHSequence"];
  MakeAppointmentMeHeySequence?: any;
  // CommonInterface
  searchedItemListWithKey?: Record<string, any>;
  errorMessage?: Record<string, any>;
  buttonLoadCheck?: Record<string, any>;
  availableSlots?: Record<string, any>[];
  divisionId?: string;
  userTokenize?: Record<string, any>;
  languageUX: "en" | "th";
};

const CARD_KEY: string = "AppointmentListMEH";

export const APPOINTMENT_ACTION = [
  "รายละเอียด",
  "ยืนยันนัดหมาย",
  "ได้รับบริการแล้ว",
  "ผู้รับบริการขอยกเลิก",
  "ผู้ให้บริการขอยกเลิก",
] as const;

export type AppointmentAction = (typeof APPOINTMENT_ACTION)[number];

const CardAppointmentListMEH = (props: CardAppointmentListMEHProps) => {
  const intl = useIntl();
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [openPopupId, setOpenPopupId] = useState<string>("");
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [openModDetail, setOpenModDetail] = useState<boolean>(false);
  const [openModalConfirm, setOpenModalConfirm] = useState<string>("");
  const [errorModalConfirm, setErrorModalConfirm] = useState<string>("");
  const [modInfoMsg, setModInfoMsg] = useState("");

  const filterAppointmentList = props.AppointmentListMEHSequence?.filterAppointmentList;

  // Use Effect
  useEffect(() => {
    props.runSequence({
      sequence: "AppointmentListMEH",
      restart: true,
      action: ACTIONS.SEARCH,
    });
  }, []);

  useEffect(() => {
    const message = props.buttonLoadCheck?.[BTN_ACTS.CONFIRM_APPOINTMENT];

    if (message && message !== "LOADING") {
      setModInfoMsg(message === "SUCCESS" ? "บันทึกสำเร็จ" : "เกิดข้อผิดพลาด");
    }
  }, [props.buttonLoadCheck]);

  const handleSelectedItem = useCallback(
    (data) => async (value: any, key: any) => {
      props.setProp(
        `AppointmentListMEHSequence.filterAppointmentList.${data.name as "patientID"}`,
        value || null,
      );
    },
    [props.searchedItemListWithKey],
  );

  const handlePageChange = useCallback(
    (_e: any, data: any) => {
      handleSearch({ activePage: data.activePage });
    },
    [props.AppointmentListMEHSequence?.appointmentListSummaryPage],
  );

  const mapOptions = useCallback(
    (items: any[]) => [
      { key: "ทั้งหมด", value: "ทั้งหมด", text: "ทั้งหมด" },
      ...items.filter(Boolean).map((item) => ({
        key: item.provider_id || item.id,
        text: `${item.pre_name || ""} ${item.first_name || ""} ${item.last_name || ""} ${
          item.name || ""
        }`,
        value: item.provider_id || item.id,
      })),
    ],
    [],
  );

  // Use Memo
  const appointmentStatusOption = useMemo(() => {
    return [
      { key: "ทั้งหมด", value: "ทั้งหมด", text: "ทั้งหมด" },
      ...(appointmentStatusOptions || []),
    ];
  }, [appointmentStatusOptions]);

  const statusOptions = useMemo(() => {
    return [{ key: "ทั้งหมด", value: "ทั้งหมด", text: "ทั้งหมด" }, ...(orderStatusOptions || [])];
  }, [orderStatusOptions]);

  const handleChecked = (item: any) => {
    const ids: string[] = item.id_list || [item.id];

    if (checkedIds.some((id: string) => ids.includes(id))) {
      setCheckedIds(checkedIds.filter((id) => !ids.includes(id)));
    } else {
      setCheckedIds([...checkedIds, ...ids]);
    }
  };

  const appointmentListData = useMemo(() => {
    return (props.AppointmentListMEHSequence?.appointmentList || [])?.map((item) => {
      return {
        ...item,
        chk: (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Checkbox
              checked={checkedIds.includes(item?.id)}
              disabled={
                item?.appointment_status_text === "ยกเลิกนัดหมาย" ||
                item?.service_status_text === "รับบริการแล้ว"
              }
              onChange={() => {
                handleChecked(item);
              }}
            />
          </div>
        ),
        id: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {item?.id || ""}
          </div>
        ),
        patientName: (
          <div>{`${item?.patient_first_name || ""} ${item?.patient_last_name || ""}`}</div>
        ),
        estimated_at: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div>{item?.estimated_at || ""}</div>
            <div
              style={{
                borderRadius: "40px",
                textAlign: "center",
                minWidth: "75px",
                fontSize: "12px",
                marginLeft: "10px",
                ...statusStyles["คิวแทรก"],
                ...(item?.type_code !== "APP_INSERT" && { display: "none" }),
              }}
            >
              {"คิวแทรก"}
            </div>
          </div>
        ),
        appointment_status_text: (
          <div
            style={{
              borderRadius: "40px",
              padding: "4px 4px 0",
              textAlign: "center",
              minWidth: "100px",
              fontSize: "14px",
              margin: "auto",
              ...statusStyles[item.appointment_status_text as appointmentStatusType],
            }}
          >
            {item.appointment_status_text}
          </div>
        ),
        service_status_text: (
          <div
            style={{
              borderRadius: "40px",
              padding: "4px 4px 0",
              textAlign: "center",
              minWidth: "100px",
              fontSize: "14px",
              margin: "auto",
              ...statusStyles[item.service_status_text as serviceStatusType],
            }}
          >
            {item.service_status_text}
          </div>
        ),
        service_date_text: (
          <div style={{ display: "flex", justifyContent: "center" }}>{`${
            item.service_date_text || "-"
          }`}</div>
        ),
        actions: (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Popup
              open={openPopupId === item.id}
              onOpen={() => setOpenPopupId(item.id)}
              onClose={() => setOpenPopupId("")}
              position="bottom right"
              on="click"
              pinned
              content={
                <div>
                  {APPOINTMENT_ACTION.filter((type: string) =>
                    item.appointment_status_text === "ยกเลิกนัดหมาย" ||
                    item.service_status_text === "รับบริการแล้ว"
                      ? type === "รายละเอียด"
                      : item.appointment_status_text === "ยืนยันนัดหมาย"
                        ? type !== "ยืนยันนัดหมาย"
                        : type,
                  ).map((type: string) => {
                    return (
                      <Button
                        content={type}
                        size="small"
                        fluid
                        style={{
                          margin: "0.25rem 0",
                          ...appointmentActionStyles[type as AppointmentAction],
                        }}
                        onClick={() => handleAppointmentAction(type, item)}
                      />
                    );
                  })}
                </div>
              }
              trigger={
                <span style={{ cursor: "pointer" }}>
                  <Icon name="ellipsis vertical" size="small" />
                </span>
              }
            />
          </div>
        ),
        division: (
          <div>
            <div
              style={{
                display: "inline-block",
                borderRadius: "10px",
                padding: "2px 10px",
                textAlign: "center",
                fontSize: "14px",
                margin: "0px auto 10px ",
                color: "#D8996F",
                backgroundColor: "#FFEDB6",
                ...(item.order_division === item.division && {
                  display: "none",
                }),
              }}
            >
              นอกหน่วยงาน
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <label style={{ color: "gray", fontWeight: "bold" }}>สั่ง: </label>
              <label>{item.order_division_name}</label>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <label style={{ color: "gray", fontWeight: "bold" }}>นัด: </label>
              <label>{item.division_name}</label>
            </div>
          </div>
        ),
      };
    });
  }, [checkedIds, openPopupId, props.AppointmentListMEHSequence?.appointmentList]);

  const handleAppointmentAction = (type: string, item: any) => {
    props.runSequence({
      sequence: "AppointmentListMEH",
      action: ACTIONS.SELECTED_APPOINTMENT_DETAIL,
      data: {
        ...item,
        type,
      },
    });

    if (type === "รายละเอียด") {
      setActiveTabIndex(0);
      setOpenModDetail(true);
    } else {
      setErrorModalConfirm("");
      setOpenModalConfirm(type);
    }
    setOpenPopupId("");
  };

  const handleChangeAppointment = (e: SyntheticEvent, v: any) => {
    const value = v.type === "checkbox" ? v.checked : v.value;

    props.setProp(`AppointmentListMEHSequence.appointmentDetail.${v.name}` as any, value || null);
  };

  const handleChangeFilterAppointment = (e: SyntheticEvent, v: any) => {
    const value = v.type === "checkbox" ? v.checked : v.value;

    props.setProp(
      `AppointmentListMEHSequence.filterAppointmentList.${v.name}` as any,
      value || null,
    );
  };

  const handleChangeDate = (name: string) => (date: string) => {
    props.setProp(
      `AppointmentListMEHSequence.filterAppointmentList.${name as "startDate" | "endDate"}`,
      date,
    );
  };

  const handleSearch = (params: any = null) => {
    props.runSequence({
      sequence: "AppointmentListMEH",
      action: ACTIONS.SEARCH,
      ...(params ? { ...params } : {}),
    });

    setCheckedIds([]);
  };

  const handleSetAppointmentFinish = () => {
    props.runSequence({
      sequence: "AppointmentListMEH",
      action: ACTIONS.SET_APPOINTMENT_FINISH,
      data: checkedIds,
    });
  };

  const handleCheckAll = (e: SyntheticEvent, v: any) => {
    if (v.checked) {
      let allId = (props.AppointmentListMEHSequence?.appointmentList || [])
        .filter(
          (item) =>
            item?.appointment_status_text !== "ยกเลิกนัดหมาย" &&
            item?.service_status_text !== "รับบริการแล้ว",
        )
        .map((item: any) => {
          return item.id;
        });

      setCheckedIds(allId);
    } else {
      setCheckedIds([]);
    }
  };

  const checkAllStatus = useMemo(() => {
    const allId = (props.AppointmentListMEHSequence?.appointmentList || []).filter(
      (item) =>
        item?.appointment_status_text !== "ยกเลิกนัดหมาย" &&
        item?.service_status_text !== "รับบริการแล้ว",
    );

    return (allId.length || 0) > 0 && checkedIds.length === allId.length;
  }, [props.AppointmentListMEHSequence?.appointmentList, checkedIds]);

  const columnsAppoitmentList = [
    {
      Header: <Checkbox checked={checkAllStatus} onChange={handleCheckAll} />,
      accessor: "chk",
      width: 80,
    },
    {
      Header: "BookingID",
      accessor: "id",
      width: 100,
    },
    {
      Header: "ชื่อ - นามสกุลผู้รับบริการ",
      accessor: "patientName",
      width: 200,
    },
    {
      Header: "เบอร์โทร",
      accessor: "patient_tel",
      width: 110,
    },
    {
      Header: "วันเวลาที่นัดหมาย",
      accessor: "estimated_at",
      width: 230,
    },
    {
      Header: "หน่วยงาน",
      accessor: "division",
      width: 200,
    },
    {
      Header: "ผู้ให้บริการ",
      accessor: "doctor_full_name",
      width: 200,
    },
    {
      Header: "สถานะนัดหมาย",
      accessor: "appointment_status_text",
      width: 150,
    },
    {
      Header: "วันที่รับบริการ",
      accessor: "service_date_text",
      width: 180,
    },
    {
      Header: "สถานะรับบริการ",
      accessor: "service_status_text",
      width: 150,
    },
    {
      Header: "Actions",
      accessor: "actions",
      width: 80,
    },
  ];

  const panes = useMemo(() => {
    const tabData = [
      {
        menuItem: {
          content: (
            <div style={{ alignItems: "center", display: "flex", gap: "8px" }}>
              <div>รายละเอียดการนัดหมาย</div>
            </div>
          ),
        },
        render: () => (
          <Tab.Pane>
            <CardAppointmentDetailMEH
              runSequence={props.runSequence}
              setProp={props.setProp}
              AppointmentListMEHSequence={props.AppointmentListMEHSequence}
              MakeAppointmentMeHeySequence={props.MakeAppointmentMeHeySequence}
              availableSlots={props.availableSlots}
              onUpdateAppointment={handleUpdateAppointment}
              onOpenPackageDetail={handleOpenPackageDetail}
              onOpenModDetail={(type: boolean) => setOpenModDetail(type)}
              buttonLoadCheck={props.buttonLoadCheck}
              languageUX={props.languageUX}
            />
          </Tab.Pane>
        ),
      },
    ];

    if (
      props.AppointmentListMEHSequence?.appointmentDetail?.appointment_status_text !==
        "ยกเลิกนัดหมาย" &&
      props.AppointmentListMEHSequence?.appointmentDetail?.service_status_text !== "รับบริการแล้ว"
    ) {
      tabData.push({
        menuItem: {
          content: (
            <div style={{ alignItems: "center", display: "flex", gap: "8px" }}>
              <div>เลือกวันเวลานัดหมาย</div>
            </div>
          ),
        },
        render: () => (
          <Tab.Pane>
            <div style={{}}>
              <CardMakeAppointmentMeHey
                controller={props.controller}
                runSequence={props.runSequence}
                onEvent={props.onEvent}
                setProp={props.setProp}
                MakeAppointmentMeHeySequence={props.MakeAppointmentMeHeySequence}
                searchedItemListWithKey={props.searchedItemListWithKey}
                isAppointmentList={true}
                disableWheelNavigation={true}
                onAppointmentClick={(data) => {
                  props.runSequence({
                    sequence: "AppointmentListMEH",
                    action: ACTIONS.ASSIGN_APPOINTMENT_TO_BLOCK,
                    data,
                  });

                  setActiveTabIndex(0);
                }}
                languageUX={props.languageUX}
              />
            </div>
          </Tab.Pane>
        ),
      });
    }
    return tabData;
  }, [
    props.AppointmentListMEHSequence?.appointmentDetail,
    props.AppointmentListMEHSequence?.packageDetail,
    props.availableSlots,
    activeTabIndex,
    props.MakeAppointmentMeHeySequence,
    props.buttonLoadCheck,
  ]);

  const handleTabChange = (e: SyntheticEvent, data: TabProps) => {
    setActiveTabIndex(data.activeIndex as number);
  };

  const handleConfirmAppointment = () => {
    if (["ผู้รับบริการขอยกเลิก", "ผู้ให้บริการขอยกเลิก"].includes(openModalConfirm)) {
      const statusNote = props.AppointmentListMEHSequence?.appointmentDetail?.statusNote;

      if (!statusNote || statusNote.trim().length === 0) {
        setErrorModalConfirm("กรุณาระบุเหตุผลในการยกเลิกนัดหมาย");
        return;
      }

      if (!props.userTokenize?.employeeName) {
        setErrorModalConfirm("ระบุรหัส ID");

        return;
      }
    }

    props.runSequence({
      sequence: "AppointmentListMEH",
      action: ACTIONS.CONFIRM_APPOINTMENT,
      data: {
        type: openModalConfirm,
      },
      callback: (type: string) => {
        setOpenModalConfirm(type);
      },
    });
  };

  const closeAppointmentFinish = () => {
    setCheckedIds([]);
    props.setProp("AppointmentListMEHSequence.appointmentFinish", []);
  };

  const handleAppointmentFinish = () => {
    props.runSequence({
      sequence: "AppointmentListMEH",
      action: ACTIONS.CONFIRM_APPOINTMENT_FINISH,
      callback: () => {
        setCheckedIds([]);
      },
    });
  };

  const handleUpdateAppointment = () => {
    props.runSequence({
      sequence: "AppointmentListMEH",
      action: ACTIONS.UPDATE_APPOINTMENT,
      callback: (type: boolean) => {
        setOpenModDetail(type);
      },
    });
  };

  const handleOpenPackageDetail = (packageId: number) => {
    props.runSequence({
      sequence: "AppointmentListMEH",
      action: ACTIONS.GET_PACKAGE_DETAIL,
      packageId,
    });
  };

  const handleSelect: SelectedItemHandler = (value, key, data) => {
    props.setProp(`AppointmentListMEHSequence.filterAppointmentList.providerID`, value);
  };

  const handleCloseModInfo = () => {
    setModInfoMsg("");

    props.setProp(`buttonLoadCheck.${BTN_ACTS.CONFIRM_APPOINTMENT}`, null);
  };

  const getSubtitleText = () => {
    const type = props.AppointmentListMEHSequence?.appointmentDetail?.type;
    switch (type) {
      case "ยืนยันนัดหมาย":
        return "ยืนยันรายการนัดหมาย";
      case "ได้รับบริการแล้ว":
        return "ยืนยันการให้บริการ";
      case "ผู้รับบริการขอยกเลิก":
      case "ผู้ให้บริการขอยกเลิก":
        return "ยกเลิกรายการนัดหมาย";
      default:
        return "";
    }
  };

  return (
    <div className="card-appointment-list" style={{ margin: "10px 0px 0px 0px" }}>
      <SnackMessage
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_KEY}`, null);
        }}
        error={props.errorMessage?.[CARD_KEY]}
        languageUX={props.languageUX}
      />

      <CardAppointmentListMEHUX
        titleName={"รายชื่อผู้นัดหมาย"}
        appointmentList={props.AppointmentListMEHSequence}
        appointmentStatusOptions={appointmentStatusOption}
        orderStatusOptions={statusOptions}
        columnsAppoitmentList={columnsAppoitmentList}
        appointmentListData={appointmentListData}
        onChangeData={handleChangeFilterAppointment}
        onChangeDate={handleChangeDate}
        searchBoxDoctor={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="AL"
            type="Provider"
            valueKey="provider_id"
            placeholder={"ผู้ให้บริการ"}
            searchedItemListWithKey={props.searchedItemListWithKey}
            selectedItem={filterAppointmentList?.providerID}
            mapOptions={mapOptions}
            setSelected={handleSelect}
            onAdditionalUrlParams={() => ({ division: props.divisionId })}
          />
        }
        searchBoxPatient={
          <SearchBoxDropdown
            onEvent={props.onEvent}
            // config
            type="OfficialAccountPatientList"
            id="AL"
            style={{ width: "100%" }}
            fluid
            useWithKey
            inline
            icon="search"
            placeholder={"ค้นหาชื่อ - นามสกุล"}
            limit={20}
            // Select
            mapOptions={mapOptions}
            searchedItemListWithKey={props.searchedItemListWithKey}
            selectedItem={filterAppointmentList?.patientID || null}
            setSelectedItem={handleSelectedItem({
              name: "patientID",
            })}
            onAdditionalUrlParams={() => ({ active: true })}
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={BTN_ACTS.SEARCH}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            // config
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid={true}
            size="small"
            title={"ค้นหาข้อมูล"}
          />
        }
        buttonAppointmentFinish={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSetAppointmentFinish}
            // data
            disabled={checkedIds.length === 0}
            paramKey={BTN_ACTS.SET_APPOINTMENT_FINISH}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SET_APPOINTMENT_FINISH]}
            // config
            style={{ color: "rgba(145, 197, 97, 1)", backgroundColor: "rgba(84, 174, 0, 0.15)" }}
            fluid={true}
            size="small"
            title={"รับบริการแล้ว"}
          />
        }
        pagination={
          <Pagination
            // className="StockManagement-pagination"
            activePage={
              props.AppointmentListMEHSequence?.appointmentListSummaryPage?.activePage || 0
            }
            ellipsisItem={{
              content: <Icon name="ellipsis horizontal" />,
              icon: true,
            }}
            firstItem={{
              content: <Icon name="angle double left" />,
              icon: true,
            }}
            lastItem={{
              content: <Icon name="angle double right" />,
              icon: true,
            }}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
            totalPages={props.AppointmentListMEHSequence?.appointmentListSummaryPage?.total || 0}
            size="mini"
            // callback
            onPageChange={handlePageChange}
          />
        }
      />

      <Modal
        className="modal-appointment"
        open={openModDetail}
        closeOnDimmerClick={true}
        onClose={() => setOpenModDetail(false)}
        size="large"
      >
        <Tab
          activeIndex={activeTabIndex}
          menu={{ pointing: true, secondary: true }}
          panes={panes}
          onTabChange={handleTabChange}
        />
      </Modal>

      <Modal
        size="small"
        open={APPOINTMENT_ACTION.includes(openModalConfirm)}
        closeOnDimmerClick={true}
        onClose={(e: any) => setOpenModalConfirm("")}
      >
        <div style={{ margin: "20px" }}>
          <ErrorMessage error={errorModalConfirm} />
          <Form>
            <FormGroup style={{ display: "flex", justifyContent: "center" }}>
              <FormField>
                <label
                  style={{
                    fontSize: "16px",
                    color: "var(--primary-theme-color)",
                  }}
                >
                  {openModalConfirm === "ยืนยันนัดหมาย"
                    ? "ยืนยันรายการนัดหมาย"
                    : openModalConfirm === "ได้รับบริการแล้ว"
                      ? "ยืนยันการให้บริการเสร็จสิ้น"
                      : openModalConfirm === "ผู้รับบริการขอยกเลิก"
                        ? "ยืนยันยกเลิกนัดหมายจากผู้รับบริการ"
                        : openModalConfirm === "ผู้ให้บริการขอยกเลิก"
                          ? "ยืนยันยกเลิกนัดหมายจากผู้ให้บริการ"
                          : ""}
                </label>
              </FormField>
            </FormGroup>
            <FormGroup style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
              <FormField width={6}>
                <label>{"ชื่อ - นามสกุลผู้รับบริการ"}</label>
              </FormField>
              <FormField width={6}>
                <div>{props.AppointmentListMEHSequence?.appointmentDetail?.patient}</div>
              </FormField>
            </FormGroup>
            <FormGroup style={{ display: "flex", justifyContent: "center" }}>
              <FormField width={6}>
                <label>{"วันเวลาที่นัดหมาย"}</label>
              </FormField>
              <FormField width={6}>
                <div>{props.AppointmentListMEHSequence?.appointmentDetail?.estimated_at}</div>
              </FormField>
            </FormGroup>
            <FormGroup style={{ display: "flex", justifyContent: "center" }}>
              <FormField width={6}>
                <label>{"ผู้ให้บริการ"}</label>
              </FormField>
              <FormField width={6}>
                <div> {props.AppointmentListMEHSequence?.appointmentDetail?.doctor_full_name}</div>
              </FormField>
            </FormGroup>
            {["ผู้รับบริการขอยกเลิก", "ผู้ให้บริการขอยกเลิก"].includes(openModalConfirm) && (
              <Form>
                <FormGroup style={{ display: "flex", justifyContent: "center" }}>
                  <FormField width={12}>
                    <label>
                      {"เหตุผลการยกเลิก"}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                  </FormField>
                </FormGroup>
                <FormGroup style={{ display: "flex", justifyContent: "center" }}>
                  <FormField width={12} style={{ width: "100%" }}>
                    <TextArea
                      name="statusNote"
                      onChange={handleChangeAppointment}
                      rows={3}
                      style={{ resize: "none", width: "100%" }}
                      placeholder="กรุณาระบุเหตุผลในการยกเลิกนัดหมาย"
                      value={props.AppointmentListMEHSequence?.appointmentDetail?.statusNote}
                    ></TextArea>
                  </FormField>
                </FormGroup>
                <FormGroup style={{ display: "flex", justifyContent: "center" }}>
                  <FormField width={12} style={{ display: "flex", alignItems: "center" }}>
                    <label style={{ minWidth: "max-content", marginRight: "10px" }}>
                      {"ระบุรหัส ID"}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <div style={{ width: "100%" }}>
                      <EmployeeToken
                        placeholder={"ระบุรหัส ID & กด Enter"}
                        onEnterToken={(code) => {
                          props.onEvent({
                            message: "UserTokenize",
                            params: {
                              action: "CheckUserToken",
                              code: code,
                            },
                          });
                        }}
                        onClearToken={() =>
                          props.setProp("userTokenize", {
                            ...props.userTokenize,
                            token: "",
                            employeeName: "",
                            loading: false,
                            error: null,
                          })
                        }
                        error={!!props.userTokenize?.error}
                        loading={props.userTokenize?.loading}
                        employeeName={props.userTokenize?.employeeName}
                      />
                    </div>
                  </FormField>
                </FormGroup>
              </Form>
            )}
            <FormGroup style={{ marginTop: "40px", display: "flex", justifyContent: "center" }}>
              <FormField>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ margin: "0px 10px" }}>
                    <Button
                      onClick={() => {
                        setOpenModalConfirm("");
                        props.setProp("AppointmentListMEHSequence.appointmentDetail", {});
                      }}
                    >
                      ปิดหน้าจอ
                    </Button>
                  </div>
                  <div style={{ margin: "0px 10px" }}>
                    <ButtonLoadCheck
                      // function
                      setProp={props.setProp}
                      onClick={handleConfirmAppointment}
                      // data
                      paramKey={BTN_ACTS.CONFIRM_APPOINTMENT_FINISH}
                      buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.CONFIRM_APPOINTMENT_FINISH]}
                      // config
                      style={{ background: "var(--primary-theme-color)", color: "white" }}
                      fluid={true}
                      size="small"
                      title={"บันทึกข้อมูล"}
                    />
                  </div>
                </div>
              </FormField>
            </FormGroup>
          </Form>
        </div>
      </Modal>

      <Modal
        size="small"
        open={
          props.AppointmentListMEHSequence?.appointmentFinish &&
          props.AppointmentListMEHSequence?.appointmentFinish?.length !== 0
        }
        closeOnDimmerClick={true}
        onClose={closeAppointmentFinish}
      >
        <div style={{ margin: "20px" }}>
          <Form>
            <FormGroup style={{ display: "flex", justifyContent: "center" }}>
              <FormField>
                <label
                  style={{
                    fontSize: "16px",
                    color: "var(--primary-theme-color)",
                  }}
                >
                  {"ยืนยันการให้บริการเสร็จสิ้น"}
                </label>
              </FormField>
            </FormGroup>
            <FormGroup style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
              <FormField width={5}>
                <label>{"ชื่อ - นามสกุลผู้รับบริการ"}</label>
              </FormField>
              <FormField width={5}>
                <label>{"วันเวลาที่นัดหมาย"}</label>
              </FormField>
              <FormField width={5}>
                <label>{"ผู้ให้บริการ"}</label>
              </FormField>
            </FormGroup>
            {(props.AppointmentListMEHSequence?.appointmentFinish || []).map((item) => {
              return (
                <FormGroup style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
                  <FormField width={5}>
                    <div>{item?.patientName}</div>
                  </FormField>
                  <FormField width={5}>
                    <div>{item?.estimatedAt}</div>
                  </FormField>
                  <FormField width={5}>
                    <div>{item?.doctorName}</div>
                  </FormField>
                </FormGroup>
              );
            })}

            <FormGroup style={{ marginTop: "40px", display: "flex", justifyContent: "center" }}>
              <FormField>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ margin: "0px 10px" }}>
                    <Button onClick={closeAppointmentFinish}>ปิดหน้าจอ</Button>
                  </div>
                  <div style={{ margin: "0px 10px" }}>
                    <ButtonLoadCheck
                      // function
                      setProp={props.setProp}
                      onClick={handleAppointmentFinish}
                      // data
                      paramKey={BTN_ACTS.CONFIRM_APPOINTMENT_FINISH}
                      buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.CONFIRM_APPOINTMENT_FINISH]}
                      // config
                      style={{ background: "var(--primary-theme-color)", color: "white" }}
                      fluid={true}
                      size="small"
                      title={"บันทึกข้อมูล"}
                    />
                  </div>
                </div>
              </FormField>
            </FormGroup>
          </Form>
        </div>
      </Modal>

      <ModInfoOA
        open={!!modInfoMsg}
        type={modInfoMsg.includes("บันทึกสำเร็จ") ? "success" : "error"}
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
        style={{ minWidth: "600px" }}
        btnText={"ปิดหน้าจอ"}
        btnStyle={{ backgroundColor: "gray" }}
        subtitle={
          <div>
            <label
              style={{
                color: "black",
              }}
            >
              {getSubtitleText()}
            </label>
            <label
              style={{
                color: "var(--primary-theme-color)",
              }}
            >
              สำเร็จ
            </label>
            <Form>
              {[
                {
                  header: "ชื่อ - นามสกุลผู้รับบริการ",
                  data: props.AppointmentListMEHSequence?.appointmentDetail?.patient,
                },
                {
                  header: "วันเวลาที่นัดหมาย",
                  data: props.AppointmentListMEHSequence?.appointmentDetail?.estimated_at,
                },
                {
                  header: "ผู้ให้บริการ",
                  data: props.AppointmentListMEHSequence?.appointmentDetail?.doctor_full_name,
                },
              ].map((item) => {
                return (
                  <FormGroup
                    style={{
                      margin: "10px 0px 0px 30px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <FormField width={6} style={{ display: "flex", justifyContent: "flex-start" }}>
                      <label>{item.header}</label>
                    </FormField>
                    <FormField width={6} style={{ display: "flex", justifyContent: "flex-start" }}>
                      <label>{item.data}</label>
                    </FormField>
                  </FormGroup>
                );
              })}
            </Form>
          </div>
        }
      />
    </div>
  );
};

CardAppointmentListMEH.displayName = "CardAppointmentListMEH";
export default CardAppointmentListMEH;

export const statusStyles = {
  ยืนยันนัดหมาย: {
    color: "rgba(145, 197, 97, 1)",
    backgroundColor: "rgba(84, 174, 0, 0.15)",
  },
  ยกเลิกนัดหมาย: {
    color: "rgba(255, 0, 0, 1)",
    backgroundColor: "rgba(174, 0, 0, 0.15)",
  },
  รอยืนยัน: {
    color: "rgba(140, 88, 14, 1)",
    backgroundColor: "rgba(255, 222, 175, 1)",
  },
  รับบริการแล้ว: {
    color: "rgba(145, 197, 97, 1)",
    backgroundColor: "rgba(84, 174, 0, 0.15)",
  },
  ยังไม่ได้รับบริการ: {
    color: "rgba(140, 88, 14, 1)",
    backgroundColor: "rgba(255, 222, 175, 1)",
  },
  ยกเลิก: {
    color: "rgba(255, 0, 0, 1)",
    backgroundColor: "rgba(174, 0, 0, 0.15)",
  },
  ถูกเลื่อนนัด: {
    color: "#D79E42",
    backgroundColor: "#FCF5DA",
  },
  คิวแทรก: {
    color: "rgb(243, 130, 160)",
    border: "3px solid rgb(243, 130, 160)",
  },
};

export const appointmentActionStyles = {
  รายละเอียด: {
    color: "rgba(255, 255, 255, 255)",
    backgroundColor: "#F382A0",
  },
  ยืนยันนัดหมาย: {
    color: "#8A5C2F",
    backgroundColor: "#F8D5A3",
  },
  ได้รับบริการแล้ว: {
    color: "#7E9768",
    backgroundColor: "#CDE3B9",
  },
  ผู้รับบริการขอยกเลิก: {},
  ผู้ให้บริการขอยกเลิก: {},
};
